import {
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	Form,
	Tabs,
	ToolbarAction,
	ValidationError
} from "client/Components";
import {
	attachToUserAndUserGroupCollection
} from "client/Features/Users/UserAndUserGroupCollection/attachable";
import {
	UserAndUserGroupCollection,
	UserAndUserGroupKind
} from "client/Features/Users/UserAndUserGroupCollection/UserAndUserGroupCollection";
import { ActiveTab } from "client/Features/Users/UserAndUserGroupTabSelector/UserAndUserGroupTabSelector";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	isNil,
	isNotNil
} from "client/Misc/utils";
import { tryParseInt } from "client/Misc/parseUtils";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IRootValidation,
	IValidationConfiguration,
} from "client/Validation";
import {
	hasMaxSize,
	hasMinimumContrastRatio,
	inline,
	isNotEmpty,
	isNotNullNorUndefined,
	isSupportedFileType,
} from "client/Validation/rules";
import {
	configureValidationStep,
	IObjectValidationStepBuilder
} from "client/Validation/steps";
import { ActionKind, buildActionsForTopBar } from "client_legacy/ndaw/actions";
import { localize } from "client_legacy/ndaw/culture";
import {
	addBranding,
	BrandingImageConstraints,
	BrandingSettingsListItemModel,
	BrandingSettingsSaveModel,
	BrandingSettingsViewModel,
	BrandingViewModelConfig,
	ContrastColorStyle,
	DarkThemeSettingsSaveModel,
	getCreateViewModel,
	getEditViewModel,
	LiveTileHeaderStyle,
	updateBranding,
	WorkspaceHeaderStyle
} from "client/Api/Branding/branding";
import { SettingsPage } from "../../SettingsPage";
import { UsersAndUserGroupsDataModel } from "client/Api/UserGroupsManagement/userGroupsManagement";
import { ThemesTab } from "./ThemesTab";
import { SettingsTab } from "./SettingsTab";
import { ColorContrastRequirements } from "client/Components/ColorPicker/BaseColorPicker";
import styles from "./BrandingSettingsForm.module.less";
import { useActiveSettingsMenuItem } from "../../SidebarMenu/sidebarMenuSelectionStore";
import { PathRedirect } from "client/Validation/serverErrors";
import { DotPathWildcardPattern } from "client/Misc/dotPathWildcardPattern";
import {
	NavigationCallback,
	PredefinedPropOptionKind,
	RouteParametersFromProps,
	connectToRouter,
	createBrowserNavigationUrl
} from "client/Navigation";
import { brandingRouting } from "../Overview/BrandingOverview";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import classNames from "classnames";
import { Base64FileUriUploadWithMetadataModel } from "client/Misc/commonTypes";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

const {
	Tab
} = Tabs;

export const darkThemePrimaryColorContrastRequirements: ColorContrastRequirements = {
	minimumRequiredContrastRatio: 4.5,
	compareAgainst: [
		"#202020",
		"#303030"
	]
};

const staticValidationConfiguration: IValidationConfiguration<FormData> =
	createValidationConfiguration<FormData>();

staticValidationConfiguration
	.forScalar("brandingName")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

staticValidationConfiguration.forObject("darkThemeSettings")
	.forScalar("primaryColor")
	.add(
		hasMinimumContrastRatio(darkThemePrimaryColorContrastRequirements)
	);

staticValidationConfiguration.forObject("darkThemeSettings")
	.forScalar("textLinkColor")
	.add(
		hasMinimumContrastRatio(darkThemePrimaryColorContrastRequirements)
	);

const settingsValidationStep: IObjectValidationStepBuilder<FormData> = configureValidationStep<FormData>()
	.add(
		"isDefault",
		"brandingName",
		"productName",
		"supportContactDetails",
		"customHomeButtonUrl"
	);

const themesValidationStep: IObjectValidationStepBuilder<FormData> = configureValidationStep<FormData>()
	.add(
		"headerColor",
		"workspaceHeaderStyle",
		"primaryColor",
		"textLinkColor",
		"liveTileHeaderStyle",
		"workspaceBackground",
		"workspaceLogo",
		"publicPageLogo",
		"homeLogo",
		"darkThemeSettings"
	);

const usersValidationStep: IObjectValidationStepBuilder<FormData> = configureValidationStep<FormData>()
	.add("targetAudience");

export type BrandingSettingFormProps = {
	id?: number;
	onNavigate: NavigationCallback;
};

export const BrandingSettingForm = (props: BrandingSettingFormProps) => {
	const {
		id,
		onNavigate
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [state, setState] = useState<BrandingSettingState>(DEFAULT_STATE);
	const [formData, setFormData] = useState<FormData>(DEFAULT_FORM_DATA);

	const {
		usersAndUserGroupsData,
		defaultBranding,
		originalBrandingName,
		darkThemeHomeLogoId,
		darkThemeSignInLogoId,
		darkThemeWorkspaceLogoId,
		logoConstraints,
		backgroundConstraints,
		homeLogoId,
		publicPageLogoId,
		workspaceLogoId,
		workspaceBackgroundId,
		shouldRenderUrl,
		isCustomHomeButtonUrlEnabled
	} = state;

	const [hasCustomBackgroundImage, setHasCustomBackgroundImage] = useState<boolean>(false);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	useActiveSettingsMenuItem(SettingsPageKind.Branding);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(id: number | undefined, signal: AbortSignal): Promise<void> {
				setIsBusy(true);

				try {
					const viewModel: BrandingViewModelConfig = isNotNil(id)
						? await getEditViewModel(id, signal)
							.catch(handlePageDataLoadingError)
						: await getCreateViewModel(signal)
							.catch(handlePageDataLoadingError);

					const formData: FormData = convertToSaveModel(viewModel.model);
					const state: BrandingSettingState = createInitialState(viewModel);
					setFormData(formData);
					setState(state);

					setHasCustomBackgroundImage(
						formData.workspaceBackground.isSet
							? isNotNil(formData.workspaceBackground.value)
							: isNotNil(state.workspaceBackgroundId)
					);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					console.error(e);
				}

				setIsBusy(false);
			}

			const cancellation = new AbortController();

			fetchData(id, cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager, id]
	);

	const validationConfiguration = useMemo(
		(): IValidationConfiguration<FormData> => {
			const {
				maxFileSize: logoMaxFileSize,
				supportedImageTypes: supportedLogoImageTypes
			} = logoConstraints;

			let result: IValidationConfiguration<FormData> = staticValidationConfiguration;

			result = result.clone();

			result.forScalar("homeLogo").add(
				hasMaxSize(logoMaxFileSize),
				isSupportedFileType(
					supportedLogoImageTypes,
					localize("_InvalidLogoUpload")
				)
			);
			result.forScalar("publicPageLogo").add(
				hasMaxSize(logoMaxFileSize),
				isSupportedFileType(
					supportedLogoImageTypes,
					localize("_InvalidLogoUpload")
				)
			);
			result.forScalar("workspaceLogo").add(
				hasMaxSize(logoMaxFileSize),
				isSupportedFileType(
					supportedLogoImageTypes,
					localize("_InvalidLogoUpload")
				)
			);
			result.forScalar("workspaceBackground").add(
				hasMaxSize(backgroundConstraints.maxFileSize),
				isSupportedFileType(
					backgroundConstraints.supportedImageTypes,
					localize("_InvalidBackgroundUpload")
				)
			);

			result.forObject("darkThemeSettings")
				.forScalar("homeLogo").add(
					hasMaxSize(logoMaxFileSize),
					isSupportedFileType(
						supportedLogoImageTypes,
						localize("_InvalidLogoUpload")
					)
				);
			result.forObject("darkThemeSettings")
				.forScalar("publicPageLogo").add(
					hasMaxSize(logoMaxFileSize),
					isSupportedFileType(
						supportedLogoImageTypes,
						localize("_InvalidLogoUpload")
					)
				);
			result.forObject("darkThemeSettings")
				.forScalar("workspaceLogo").add(
					hasMaxSize(logoMaxFileSize),
					isSupportedFileType(
						supportedLogoImageTypes,
						localize("_InvalidLogoUpload")
					)
				);

			if (hasCustomBackgroundImage) {
				result.forScalar("workspaceBackground")
					.add(
						inline(
							(value): InputValidationResult => {
								if (value.isSet) {
									if (isNotNil(value)) {
										return {
											kind: InputValidationResultKind.Valid
										};
									}

									return {
										kind: InputValidationResultKind.Invalid,
										errorMessage: localize("_IsRequired")
									};
								}

								if (isNotNil(workspaceBackgroundId)) {
									return {
										kind: InputValidationResultKind.Valid
									};
								}


								return {
									kind: InputValidationResultKind.Invalid,
									errorMessage: localize("_IsRequired")
								};
							},
						)
					);
			}

			return result;
		},
		[
			backgroundConstraints,
			logoConstraints,
			hasCustomBackgroundImage,
			workspaceBackgroundId
		]
	);

	const formHandler: IFormHandler<BrandingSettingsSaveModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const validationRef: RefObject<IRootValidation<BrandingSettingsSaveModel>> = useRef<
		IRootValidation<BrandingSettingsSaveModel>
	>(
		formHandler.validation
	);

	useEffect(
		() => {
			validationRef.current = formHandler.validation;
		},
		[formHandler.validation]
	);

	const handleSubmit = useCallback(
		async (): Promise<void> => {
			const validationResult: InputValidationResult = await validationRef.current.validate();

			if (validationResult.kind === InputValidationResultKind.Invalid) {
				return;
			}

			try {
				setIsBusy(true);

				const data: BrandingSettingsSaveModel = prepareForSaving(
					formData,
					shouldRenderUrl,
					hasCustomBackgroundImage,
					isNotNil(workspaceBackgroundId)
				);

				if (isNil(id)) {
					await addBranding(data, globalCancellation);
				} else {
					await updateBranding(
						id,
						data,
						globalCancellation
					);
				}

				alertManager.showSuccess(localize("_ChangesSaved"));

				// NOTE: enforce browser navigation, to reload the page and apply the new branding
				navigateTo(
					createBrowserNavigationUrl(brandingRouting.overview)
				);
			} catch (e) {
				const isHandled: boolean = validationRef.current
					.tryProcessServerError(
						e,
						pathRedirects
					);

				if (!isHandled) {
					alertManager.showError(localize("_ChangesNotSaved"));
				}
				setIsBusy(false);
			}
		},
		[
			formData,
			shouldRenderUrl,
			hasCustomBackgroundImage,
			workspaceBackgroundId,
			id,
			alertManager,
			globalCancellation
		]
	);

	const backToOverviewHandler = useCallback(
		(): void => {
			onNavigate(brandingRouting.overview);
		},
		[onNavigate]
	);

	const defaultActions = useMemo(
		(): readonly ToolbarAction<undefined>[] => [
			{
				kind: ActionKind.Regular,
				name: "submit",
				title: localize("_Save"),
				canExecute: () => true,
				execute: handleSubmit,
				icon: "floppy-disk",
				isPrimary: true
			},
			{
				kind: ActionKind.Regular,
				name: "cancel",
				title: localize("_Cancel"),
				canExecute: () => true,
				execute: backToOverviewHandler,
				icon: "xmark",
			}
		],
		[handleSubmit, backToOverviewHandler]
	);

	const topBarActions = useMemo(
		(): readonly ToolbarAction<undefined>[] => {
			return buildActionsForTopBar<undefined, ToolbarAction<undefined>>(
				{
					defaultActions,
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive
		]
	);

	const changeShouldRenderUrlHandler = useCallback(
		(value: boolean): void => setState(
			state => ({...state, shouldRenderUrl: value})
		),
		[]
	);

	return (
		<SettingsPage
			hasBusyIndicator={isBusy}
			onBack={backToOverviewHandler}
			actions={topBarActions}
			actionContext={undefined}
			title={originalBrandingName}
		>

			<SettingsPage.Section height="stretch">
				<Form onSubmit={handleSubmit} height="fill">
					<Tabs height="fill">
						<Tab
							{...formHandler
								.forStep(settingsValidationStep)
								.attachToTab()}
							id="settings"
							title={localize("_Settings")}
						>
							<div
								className={classNames(
									styles.tabContent,
									styles.tabContent_widthConstrainted
								)}
							>
								<SettingsTab
									attachable={formHandler}
									defaultBranding={defaultBranding}
									isDefault={formData.isDefault}
									isCustomHomeButtonUrlEnabled={isCustomHomeButtonUrlEnabled}
									shouldRenderUrl={shouldRenderUrl}
									onShouldRenderUrlChange={changeShouldRenderUrlHandler}
								/>
							</div>
						</Tab>
						<Tab
							{...formHandler
								.forStep(themesValidationStep)
								.attachToTab()}
							id="dark-theme"
							title={localize("_Themes")}
						>
							<div
								className={classNames(
									styles.tabContent,
									styles.tabContent_widthConstrainted
								)}
							>
								<ThemesTab
									attachable={formHandler}
									darkThemeHomeLogoId={darkThemeHomeLogoId}
									darkThemePublicPageLogoId={darkThemeSignInLogoId}
									darkThemeWorkspaceLogoId={darkThemeWorkspaceLogoId}
									logoConstraints={logoConstraints}
									backgroundConstraints={backgroundConstraints}
									homeLogoId={homeLogoId}
									publicPageLogoId={publicPageLogoId}
									hasCustomBackgroundImage={hasCustomBackgroundImage}
									onHasCustomBackgroundImageChange={setHasCustomBackgroundImage}
									workspaceBackgroundId={workspaceBackgroundId}
									workspaceLogoId={workspaceLogoId}
									darkThemePrimaryColorContrastRequirements={darkThemePrimaryColorContrastRequirements}
								/>
							</div>
						</Tab>
						{!formData.isDefault && (
							<Tab
								{...formHandler
									.forStep(usersValidationStep)
									.attachToTab()}
								id="users"
								title={localize("_GroupsAndUsers")}
							>
								<div className={styles.tabContent}>
									<UserAndUserGroupCollection
										activeTab={ActiveTab.UserGroup}
										{...attachToUserAndUserGroupCollection(
											formHandler.for("targetAudience"),
											{
												kind: UserAndUserGroupKind.Edit,
												dialogTitleWhenAddingUsersAndUserGroups: localize("_AddGroupsOrUsers")
											}
										)}
										allUsersAndUserGroupsData={usersAndUserGroupsData}
									/>
									<ValidationError
										{...formHandler.for("targetAudience").attachToValidationError()}
									/>
								</div>
							</Tab>
						)}
					</Tabs>
				</Form>
			</SettingsPage.Section>
			<AiAssistantFabSpacing />
		</SettingsPage>
	);
};
BrandingSettingForm.displayName = "BrandingSettingForm";

export type EditBrandingSettingFormRouteParams = RouteParametersFromProps<
	BrandingSettingFormProps,
	"id"
>;

export const BrandingSettingFormForRouter = connectToRouter(
	BrandingSettingForm,
	{
		"id": {
			isRequired: false,
			tryParse: tryParseInt,
			type: "route"
		},
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);

type BrandingSettingState = {
	id: number | null;
	originalBrandingName: string;
	workspaceLogoId: string | null;
	publicPageLogoId: string | null;
	homeLogoId: string | null;
	darkThemeWorkspaceLogoId: string | null;
	darkThemeSignInLogoId: string | null;
	darkThemeHomeLogoId: string | null;
	workspaceBackgroundId: string | null;
	logoConstraints: BrandingImageConstraints;
	backgroundConstraints: BrandingImageConstraints;
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
	defaultBranding: BrandingSettingsListItemModel | undefined;
	shouldRenderUrl: boolean;
	isCustomHomeButtonUrlEnabled: boolean;
};

const DEFAULT_STATE: BrandingSettingState = {
	id: null,
	originalBrandingName: "",
	workspaceLogoId: null,
	publicPageLogoId: null,
	homeLogoId: null,
	darkThemeWorkspaceLogoId: null,
	darkThemeSignInLogoId: null,
	darkThemeHomeLogoId: null,
	workspaceBackgroundId: null,
	logoConstraints: {
		supportedImageTypes: [],
		maxFileSize: 0
	},
	backgroundConstraints: {
		supportedImageTypes: [],
		maxFileSize: 0
	},
	usersAndUserGroupsData: {
		users: [],
		userGroups: []
	},
	defaultBranding: undefined,
	shouldRenderUrl: false,
	isCustomHomeButtonUrlEnabled: false
};

type FormData = OverrideProperties<
	BrandingSettingsSaveModel,
	{
		workspaceLogo: OptionalModel<Base64FileUriUploadWithMetadataModel | null>;
		publicPageLogo: OptionalModel<Base64FileUriUploadWithMetadataModel | null>;
		homeLogo: OptionalModel<Base64FileUriUploadWithMetadataModel | null>;
		workspaceBackground: OptionalModel<Base64FileUriUploadWithMetadataModel | null>;
		darkThemeSettings: OverrideProperties<
			DarkThemeSettingsSaveModel,
			{
				workspaceLogo: OptionalModel<Base64FileUriUploadWithMetadataModel | null>;
				publicPageLogo: OptionalModel<Base64FileUriUploadWithMetadataModel | null>;
				homeLogo: OptionalModel<Base64FileUriUploadWithMetadataModel | null>;
			}
		>;
	}
>;

const DEFAULT_FORM_DATA: FormData = {
	brandingName: "",
	contrastColorStyle: ContrastColorStyle.Optimal,
	customHomeButtonUrl: null,
	headerColor: null,
	primaryColor: null,
	textLinkColor: null,
	isDefault: false,
	productName: "",
	darkThemeSettings: {
		homeLogo: { isSet: false },
		primaryColor: null,
		textLinkColor: null,
		publicPageLogo: { isSet: false },
		workspaceLogo: { isSet: false }
	},
	homeLogo: { isSet: false },
	liveTileHeaderStyle: LiveTileHeaderStyle.coloredText,
	publicPageLogo: { isSet: false },
	supportContactDetails: "",
	targetAudience: null,
	workspaceBackground: { isSet: false },
	workspaceHeaderStyle: WorkspaceHeaderStyle.coloredBackground,
	workspaceLogo: { isSet: false }
};

function createInitialState(
	initialData: BrandingViewModelConfig
): BrandingSettingState {
	const {
		backgroundConstraints,
		logoConstraints,
		model,
		isCustomHomeButtonUrlEnabled
	} = initialData;

	const {
		id,
		workspaceLogoId,
		publicPageLogoId,
		homeLogoId,
		darkThemeSettings,
		workspaceBackgroundId,
		usersAndUserGroupsData,
		defaultBranding,
		brandingName,
		customHomeButtonUrl
	} = model;

	return {
		id,
		originalBrandingName: brandingName,
		workspaceLogoId,
		publicPageLogoId,
		homeLogoId,
		darkThemeWorkspaceLogoId: darkThemeSettings
			? darkThemeSettings.workspaceLogoId
			: null,
		darkThemeSignInLogoId: darkThemeSettings
			? darkThemeSettings.workspaceSignInLogoId
			: null,
		darkThemeHomeLogoId: darkThemeSettings
			? darkThemeSettings.homeLogoId
			: null,
		workspaceBackgroundId,
		usersAndUserGroupsData,
		logoConstraints,
		backgroundConstraints,
		defaultBranding,
		shouldRenderUrl: isNotNil(customHomeButtonUrl),
		isCustomHomeButtonUrlEnabled
	};
};

function prepareForSaving(
	saveModel: BrandingSettingsSaveModel,
	shouldRenderUrl: boolean,
	hasCustomBackgroundImage: boolean,
	hasPersistedWorkspaceBackground: boolean
): BrandingSettingsSaveModel {
	return {
		...saveModel,
		targetAudience: saveModel.isDefault
			? {
				userGroupIds: [],
				userIds: []
			} : saveModel.targetAudience,
		customHomeButtonUrl: shouldRenderUrl
			? saveModel.customHomeButtonUrl
			: null,
		workspaceBackground: hasCustomBackgroundImage
			? saveModel.workspaceBackground
			: hasPersistedWorkspaceBackground
				? {
					isSet: true,
					value: null
				}
				: {
					isSet: false
				}
	};
}

function convertToSaveModel(
	model: BrandingSettingsViewModel
): FormData {
	const {
		isDefault,
		targetAudience,
		brandingName,
		productName,
		primaryColor,
		textLinkColor,
		headerColor,
		supportContactDetails,
		customHomeButtonUrl,
		liveTileHeaderStyle,
		workspaceHeaderStyle,
		darkThemeSettings,
		contrastColorStyle
	} = model;

	return {
		isDefault,
		targetAudience,
		brandingName,
		productName,
		primaryColor,
		textLinkColor,
		headerColor,
		workspaceLogo: { isSet: false },
		publicPageLogo: { isSet: false },
		homeLogo: { isSet: false },
		workspaceBackground: { isSet: false },
		supportContactDetails: supportContactDetails ?? "",
		customHomeButtonUrl,
		liveTileHeaderStyle,
		workspaceHeaderStyle,
		darkThemeSettings: {
			primaryColor: darkThemeSettings?.primaryColor,
			textLinkColor: darkThemeSettings?.textLinkColor,
			workspaceLogo: { isSet: false },
			publicPageLogo: { isSet: false },
			homeLogo: { isSet: false }
		},
		contrastColorStyle
	};
}

const pathRedirects: readonly PathRedirect[] = [
	{
		pattern: new DotPathWildcardPattern("WorkspaceLogo.Value.*?"),
		replacement: "WorkspaceLogo"
	},
	{
		pattern: new DotPathWildcardPattern("PublicPageLogo.Value.*?"),
		replacement: "PublicPageLogo"
	},
	{
		pattern: new DotPathWildcardPattern("HomeLogo.Value.*?"),
		replacement: "HomeLogo"
	},
	{
		pattern: new DotPathWildcardPattern("DarkThemeSettings.WorkspaceLogo.Value.*?"),
		replacement: "DarkThemeSettings.WorkspaceLogo"
	},
	{
		pattern: new DotPathWildcardPattern("DarkThemeSettings.PublicPageLogo.Value.*?"),
		replacement: "DarkThemeSettings.PublicPageLogo"
	},
	{
		pattern: new DotPathWildcardPattern("DarkThemeSettings.HomeLogo.Value.*?"),
		replacement: "DarkThemeSettings.HomeLogo"
	}
];